import { useState } from "react";
import { Link } from "react-router-dom";
import { getMovieById, TYPE_LABELS } from "@/lib/media";
import { useFavorites, useRecentList } from "@/hooks/useLocalLibrary";

export default function MyLibrary() {
  const [tab, setTab] = useState<"recent" | "favorites">("recent");
  const recentIds = useRecentList();
  const { favorites } = useFavorites();

  const ids = tab === "recent" ? recentIds : favorites;
  const items = ids.map((id) => getMovieById(id)).filter((movie) => movie !== undefined);

  return (
    <section className="shell mt-12 md:mt-16" aria-label="我的片单">
      <div className="rounded-lg border border-background-200 bg-background-100/70 p-4 md:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-500 text-background-50">
              <i className="ri-bookmark-3-line text-sm" />
            </span>
            <h2 className="text-base font-bold text-foreground-950">我的片单</h2>
            <span className="hidden text-xs text-foreground-500 sm:inline">记录只保存在这台设备上</span>
          </div>

          <div className="flex items-center gap-1 rounded-full bg-background-200/70 p-1">
            <button
              type="button"
              onClick={() => setTab("recent")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                tab === "recent" ? "bg-background-50 text-primary-700" : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              最近浏览 {recentIds.length > 0 && `(${recentIds.length})`}
            </button>
            <button
              type="button"
              onClick={() => setTab("favorites")}
              className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                tab === "favorites" ? "bg-background-50 text-primary-700" : "text-foreground-600 hover:text-foreground-950"
              }`}
            >
              我的收藏 {favorites.length > 0 && `(${favorites.length})`}
            </button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="mt-4 flex flex-col items-start gap-3 rounded-md border border-dashed border-background-300 bg-background-50 px-4 py-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-500">
                <i className={tab === "recent" ? "ri-history-line" : "ri-heart-line"} />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-900">
                  {tab === "recent" ? "你还没有浏览记录" : "收藏夹还是空的"}
                </p>
                <p className="mt-1 text-xs leading-relaxed text-foreground-500">
                  {tab === "recent"
                    ? "打开任意一部作品的详情页，这里会自动保留最近 5 条记录。"
                    : "在作品详情页点击「收藏作品」，之后就能从这里快速找回。"}
                </p>
              </div>
            </div>
            <Link
              to="/category/all"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              去片库挑一部
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        ) : (
          <div className="mt-4 flex gap-3 overflow-x-auto pb-1 no-scrollbar">
            {items.map((movie) => (
              <Link
                key={movie.id}
                to={`/detail/${movie.id}`}
                className="group flex w-52 shrink-0 cursor-pointer items-center gap-3 rounded-md border border-background-200 bg-background-50 p-2 transition-colors hover:border-primary-300"
              >
                <div className="h-16 w-11 shrink-0 overflow-hidden rounded bg-secondary-100">
                  <img
                    src={movie.poster}
                    alt={`《${movie.title}》封面`}
                    title={movie.title}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-1 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                    {movie.title}
                  </p>
                  <p className="mt-1 line-clamp-1 text-xs text-foreground-500">
                    {TYPE_LABELS[movie.type]} · {movie.year}
                  </p>
                  <p className="mt-1 text-[11px] text-foreground-400">评分 {movie.rating.toFixed(1)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}